import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Simple in-memory cache for contact requests
const requestCache = {
  data: null,
  timestamp: null,
  CACHE_DURATION: 3 * 60 * 1000, // 3 minutes
};

// Simple in-memory cache for bookings
const bookingCache = {
  data: null,
  timestamp: null,
  CACHE_DURATION: 3 * 60 * 1000, // 3 minutes
};

export const useContactRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRequests = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (
      !forceRefresh &&
      requestCache.data &&
      requestCache.timestamp &&
      now - requestCache.timestamp < requestCache.CACHE_DURATION
    ) {
      setRequests(requestCache.data);
      setLoading(false);
      return requestCache.data;
    }

    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/contact/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      requestCache.data = response.data;
      requestCache.timestamp = now;
      
      setRequests(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err);
      if (requestCache.data) {
        setRequests(requestCache.data);
      }
      return requestCache.data || [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const invalidateCache = useCallback(() => {
    requestCache.data = null;
    requestCache.timestamp = null;
  }, []);

  return { requests, loading, error, refetch: fetchRequests, invalidateCache };
};

export const useBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBookings = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (
      !forceRefresh &&
      bookingCache.data &&
      bookingCache.timestamp &&
      now - bookingCache.timestamp < bookingCache.CACHE_DURATION
    ) {
      setBookings(bookingCache.data);
      setLoading(false);
      return bookingCache.data;
    }

    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/bookings/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      bookingCache.data = response.data;
      bookingCache.timestamp = now;
      
      setBookings(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err);
      if (bookingCache.data) {
        setBookings(bookingCache.data);
      }
      return bookingCache.data || [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const invalidateCache = useCallback(() => {
    bookingCache.data = null;
    bookingCache.timestamp = null;
  }, []);

  return { bookings, loading, error, refetch: fetchBookings, invalidateCache };
};
